import { useNavigate } from "react-router-dom"
import { observer, useLocalObservable } from "mobx-react"
import { Button, Table, Tag, Typography } from "antd"
import { ColumnsType } from "antd/es/table"
import { choose } from "choose-matching"

import { balancesStore } from "@src/app/balanceStore"
import { LoadingBlock } from "@src/app/components/LoadingBlock"
import { NoContent } from "@src/app/components/NoContent/Index"
import { IdItem } from "@src/app/components/tableRowItems/idItem"
import { GlobalCurrenciesCollection } from "@src/app/CurrenciesCollection"
import { paths } from "@src/app/paths"
import { PaymentsEntity } from "@src/app/paymentsStore/PaymentsEntity"
import { PaymentRefundEntity } from "@src/pages/refund/classes/PaymentRefundEntity"
import { GRefundPage } from "@src/pages/refund/classes/RefundPage"
import {
  TopupPayment,
  TransactionsTopUpCollection,
} from "@src/pages/transactionsTopup/classes/TransactionsTopUpCollection"
import {
  PaymentAmlLevelEnum,
  TopupPaymentResponseStatusEnum,
} from "@src/shared/api/generated"
import { DateTimeTableCell } from "@src/shared/componemts/DateTimeTableCell"
import { TableExpandable } from "@src/shared/componemts/TableExpandable"
import { TableFilters } from "@src/shared/componemts/TableFilter"
import { WithCopy } from "@src/shared/componemts/WithCopy"
import { dateTimeFormat } from "@src/shared/date"
import { getExplorerUrl } from "@src/shared/getExplorerUrl"
import { paddingLimit } from "@src/shared/strings"
import css from "./index.module.css"

export const TransactionsTopUpPage = observer(() => {
  const transactions = useLocalObservable(
    () => new TransactionsTopUpCollection(),
  )

  const columns: ColumnsType<TopupPayment> = [
    {
      dataIndex: "id",
      render: (_, { id }) => (
        <WithCopy
          content={id}
          view={<Typography.Text>{paddingLimit(id, 0, 3)}</Typography.Text>}
        />
      ),
      title: "ID",
    },
    {
      render: (_, { topup_id }) => (
        <WithCopy
          content={topup_id}
          view={
            <Typography.Text>{paddingLimit(topup_id, 0, 3)}</Typography.Text>
          }
        />
      ),
      title: "Topup ID",
    },
    {
      render: (_, { address }) => {
        return <Typography.Text>{paddingLimit(address, 0, 3)}</Typography.Text>
      },
      title: "Address",
    },
    {
      render: (_, topUp) => {
        const operationWithUrl = getExplorerUrl(
          [topUp],
          GlobalCurrenciesCollection().items,
        )[0]

        return (
          <WithCopy
            content={topUp.tx_id}
            view={
              <Typography.Link
                href={operationWithUrl.tx_explorer_url + topUp.tx_id}
                target="_blank"
              >
                {paddingLimit(topUp.tx_id, 0, 3)}
              </Typography.Link>
            }
          />
        )
      },
      title: "Tx ID",
      width: 150,
    },
    {
      dataIndex: "amount",
      render: (_, { amount }) => {
        return (
          <Typography.Text style={{ fontWeight: 700 }}>
            {amount}
          </Typography.Text>
        )
      },
      title: "Amount",
    },
    {
      dataIndex: "network",
      title: "Network",
    },
    {
      dataIndex: "symbol",
      render: (_, { network, symbol }) => {
        const currency = GlobalCurrenciesCollection().getBySymbolNetwork({
          network,
          symbol,
        })

        return (
          <Typography.Text>{currency?.symbol_alias ?? symbol}</Typography.Text>
        )
      },
      title: "Symbol",
    },
    {
      render: (_, { status }) => {
        const cMap = {
          [TopupPaymentResponseStatusEnum.Confirmed]: "green",
          [TopupPaymentResponseStatusEnum.Failed]: "error",
          [TopupPaymentResponseStatusEnum.Pending]: "geekblue",
        }

        return <Tag color={cMap[status] ?? "geekblue"}>{status}</Tag>
      },
      title: "Status",
    },
    {
      dataIndex: "aml_level",
      render: (_, { aml_level }) => {
        const cMap = {
          [PaymentAmlLevelEnum.High]: "error",
          [PaymentAmlLevelEnum.Low]: "green",
          [PaymentAmlLevelEnum.Medium]: "warning",
        }

        return <Tag color={cMap[aml_level]}>{aml_level}</Tag>
      },
      title: "AML Level",
    },
    {
      dataIndex: "detected_at",
      render: (_, { detected_at }) => <DateTimeTableCell value={detected_at} />,
      title: "Detected At",
      width: 120,
    },
  ]

  return (
    <div className={css.container}>
      <TableFilters {...transactions.filter.props} />
      {choose(
        [transactions.isLoading && transactions.isEmpty, <LoadingBlock />],
        [
          transactions.isEmpty,
          <NoContent
            paragraph={
              transactions.filter.state.status
                ? `You have not top ups with the status ${transactions.filter.state.status}`
                : "Wait for your first top up to start receiving reports."
            }
            title="No Top ups Yet"
          />,
        ],
        [
          !transactions.isEmpty,
          <Table
            columns={columns}
            dataSource={transactions.items}
            expandable={{
              expandedRowRender: (record) => (
                <ListExpandable
                  isLoading={transactions.isLoading}
                  topUp={record}
                />
              ),
            }}
            loading={transactions.isLoading}
            pagination={transactions.pagination}
            rowKey="id"
            scroll={{ x: true }}
          />,
        ],
      )}
    </div>
  )
})
const ListExpandable = observer(
  ({ isLoading, topUp }: { isLoading: boolean; topUp: TopupPayment }) => {
    const {
      aml_level,
      amount,
      detected_at,
      id,
      network,
      status,
      symbol,
      tx_id,
    } = topUp

    const { currencies } = balancesStore

    const nav = useNavigate()

    const currency = GlobalCurrenciesCollection().getBySymbolNetwork({
      network,
      symbol,
    })

    const operationWithUrl = getExplorerUrl([topUp], currencies)[0]

    const makeRefund = () => {
      GRefundPage().setEntity(
        new PaymentRefundEntity(new PaymentsEntity(topUp)),
      )

      nav(paths.refund)
    }

    const cMap = {
      [PaymentAmlLevelEnum.High]: "error",
      [PaymentAmlLevelEnum.Low]: "green",
      [PaymentAmlLevelEnum.Medium]: "warning",
    }

    const cMapStatus = {
      [TopupPaymentResponseStatusEnum.Confirmed]: "green",
      [TopupPaymentResponseStatusEnum.Failed]: "error",
      [TopupPaymentResponseStatusEnum.Pending]: "geekblue",
    }

    return (
      <TableExpandable
        actions={
          topUp.aml_level === "high" && !topUp.refund_id ? (
            <Button
              id="GENERATED_ID_TRANSACTIONSTOPUP_BUTTON_REFUND_1"
              onClick={makeRefund}
              size={"large"}
              type={"primary"}
            >
              Refund
            </Button>
          ) : undefined
        }
        isLoading={isLoading}
        list={[
          ["ID", <WithCopy content={id} />],
          ["Topup ID", <WithCopy content={topUp.topup_id} />],
          ["Address", <WithCopy content={topUp.address} />],
          ...([
            topUp.refund_id
              ? [
                  "Refund ID",
                  <IdItem id={topUp.refund_id} p={topUp.refund_id.length} />,
                ]
              : [],
          ] as any),
          [
            "Tx ID",
            <WithCopy
              content={tx_id}
              view={
                <Typography.Link
                  href={operationWithUrl.tx_explorer_url + tx_id}
                  target="_blank"
                >
                  {tx_id}
                </Typography.Link>
              }
            />,
          ],
          [
            "Amount",
            <Typography style={{ fontWeight: 600 }}>{amount}</Typography>,
          ],
          ["Network", `${network}`],
          ["Symbol", `${currency?.symbol_alias ?? symbol}`],
          ["Status", <Tag color={cMapStatus[status]}>{status}</Tag>],
          ["AML Level", <Tag color={cMap[aml_level]}>{aml_level}</Tag>],
          ["Detected at", dateTimeFormat(detected_at as string)],
        ]}
      />
    )
  },
)
